"use client"

import * as React from "react"
import { X } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AppIcon } from "@/components/desk/AppIcon"
import { WidgetIcon } from "./WidgetIcon"
import { InstalledApp } from "@/types/workspace"
import { WidgetItem } from "./AddWidgetModal"
import { toast } from "sonner"

interface WidgetLibraryModalProps {
  isOpen: boolean
  onClose: () => void
  installedApps: InstalledApp[]
  hiddenAppIds: string[]
  setHiddenAppIds: React.Dispatch<React.SetStateAction<string[]>>
  widgetsConfig: WidgetItem[]
  setWidgetsConfig: React.Dispatch<React.SetStateAction<WidgetItem[]>>
}

export function WidgetLibraryModal({
  isOpen,
  onClose,
  installedApps,
  hiddenAppIds,
  setHiddenAppIds,
  widgetsConfig,
  setWidgetsConfig,
}: WidgetLibraryModalProps) {
  if (!isOpen) return null

  const hiddenApps = installedApps.filter(({ app }) => hiddenAppIds.includes(app.id))
  const disabledWidgets = widgetsConfig.filter((w) => !w.enabled)

  return (
    <div className="fixed inset-0 z-55 flex items-center justify-center bg-black/60 backdrop-blur-xs font-sans">
      <Card className="w-full max-w-md border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 shadow-2xl rounded-2xl space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <span>📱</span> Biblioteca de Apps e Widgets
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto max-h-[360px] pr-1">
          <div className="space-y-2">
            <h4 className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
              Widgets Disponíveis
            </h4>
            {disabledWidgets.length === 0 ? (
              <div className="text-[10px] text-zinc-450 dark:text-zinc-550 italic px-1">
                Todos os widgets estão ativos na Home.
              </div>
            ) : (
              disabledWidgets.map((widget) => (
                <div
                  key={widget.id}
                  className="flex items-center justify-between p-2.5 bg-zinc-50 dark:bg-zinc-955 rounded-xl border border-zinc-150 dark:border-zinc-850"
                >
                  <div className="flex items-center gap-2.5">
                    <WidgetIcon id={widget.id} icon={widget.icon} name={widget.name} className="size-4.5 text-[#004EB1] dark:text-blue-400" />
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-zinc-850 dark:text-zinc-200">{widget.name}</span>
                      <span className="text-[9px] text-zinc-500 dark:text-zinc-400 leading-tight">
                        {widget.description}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setWidgetsConfig((prev) =>
                        prev.map((w) => (w.id === widget.id ? { ...w, enabled: true } : w))
                      )
                      toast.success(`${widget.name} foi adicionado à tela inicial.`)
                    }}
                    className="px-2.5 py-1 bg-[#004EB1] hover:bg-[#003d8d] text-white text-[10px] font-bold rounded-lg cursor-pointer transition-colors"
                  >
                    Adicionar
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="space-y-2 border-t border-zinc-100 dark:border-zinc-800 pt-3">
            <h4 className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
              Aplicativos Ocultos
            </h4>
            {hiddenApps.length === 0 ? (
              <div className="text-[10px] text-zinc-450 dark:text-zinc-550 italic px-1">Nenhum aplicativo ocultado.</div>
            ) : (
              hiddenApps.map(({ app }) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-2.5 bg-zinc-50 dark:bg-zinc-955 rounded-xl border border-zinc-150 dark:border-zinc-850"
                >
                  <div className="flex items-center gap-2.5">
                    <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="small" />
                    <span className="text-xs font-bold text-zinc-850 dark:text-zinc-200">{app.name}</span>
                  </div>
                  <button
                    onClick={() => {
                      setHiddenAppIds((prev) => prev.filter((id) => id !== app.id))
                      toast.success(`${app.name} foi adicionado à tela inicial.`)
                    }}
                    className="px-2.5 py-1 bg-[#004EB1] hover:bg-[#003d8d] text-white text-[10px] font-bold rounded-lg cursor-pointer transition-colors"
                  >
                    Adicionar
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-100 dark:border-zinc-800 pt-4">
          <Button
            onClick={onClose}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold py-1.5 px-4 cursor-pointer"
          >
            Concluir
          </Button>
        </div>
      </Card>
    </div>
  )
}
